import { Button, Grid, Typography } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  AboutContent,
  // EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EmptyState, InfoCard } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';
import {
  EntitySonarQubeContentPage,
  SonarQubeRelatedEntitiesOverview,
} from '@backstage-community/plugin-sonarqube';
import {
  EntityJenkinsContent,
  jenkinsApiRef,
  isJenkinsAvailable,
} from '@backstage-community/plugin-jenkins';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';
import { useEntity } from '@backstage/plugin-catalog-react';
import { ArchitectureDiagramCard } from '../architecture/ArchitectureDiagramCard';
import { JenkinsSbomCard } from './JenkinsSbomCard';
import {
  CheckmarxSecurityContent,
  CheckmarxSystemOverview,
  CHECKMARX_SOURCE_LOCATION_ANNOTATION,
  isCheckmarxAvailable,
} from './CheckmarxSecurityContent';

const SONARQUBE_READ_MORE_URL =
  'https://backstage.io/docs/features/software-catalog/well-known-annotations#sonarqubeorgproject-key';

type JenkinsProjectSummary = {
  displayName: string;
  fullName: string;
  lastBuild?: {
    number: number;
    url: string;
  };
};

const JenkinsConsoleOutputCard = () => {
  const { entity } = useEntity();
  const jenkinsApi = useApi(jenkinsApiRef);
  const [projects, setProjects] = useState<JenkinsProjectSummary[]>([]);
  const [selectedJob, setSelectedJob] = useState<string>();
  const [consoleText, setConsoleText] = useState('');
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [isLoadingConsole, setIsLoadingConsole] = useState(false);
  const [error, setError] = useState<string>();

  const entityRef = {
    kind: entity.kind.toLocaleLowerCase(),
    namespace: entity.metadata.namespace ?? 'default',
    name: entity.metadata.name,
  };

  useEffect(() => {
    let isActive = true;

    const loadProjects = async () => {
      setIsLoadingProjects(true);
      setError(undefined);

      try {
        const result = (await jenkinsApi.getProjects({
          entity: entityRef,
          filter: {},
        })) as JenkinsProjectSummary[];

        if (!isActive) {
          return;
        }

        setProjects(result);
        setSelectedJob((currentSelectedJob: string | undefined) => {
          if (
            currentSelectedJob &&
            result.some(project => project.fullName === currentSelectedJob)
          ) {
            return currentSelectedJob;
          }

          return result[0]?.fullName;
        });
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Jenkins jobs.',
        );
      } finally {
        if (isActive) {
          setIsLoadingProjects(false);
        }
      }
    };

    loadProjects();

    return () => {
      isActive = false;
    };
  }, [jenkinsApi, entityRef.kind, entityRef.name, entityRef.namespace]);

  useEffect(() => {
    const selectedProject = projects.find(
      (project: JenkinsProjectSummary) => project.fullName === selectedJob,
    );

    if (!selectedProject?.lastBuild?.number) {
      setConsoleText('');
      return;
    }

    let isActive = true;

    const loadConsoleText = async () => {
      setIsLoadingConsole(true);
      setError(undefined);

      try {
        const result = await jenkinsApi.getBuildConsoleText({
          entity: entityRef,
          jobFullName: selectedProject.fullName,
          buildNumber: String(selectedProject.lastBuild?.number),
        });

        if (!isActive) {
          return;
        }

        setConsoleText(result.consoleText);
      } catch (loadError) {
        if (!isActive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load Jenkins console output.',
        );
        setConsoleText('');
      } finally {
        if (isActive) {
          setIsLoadingConsole(false);
        }
      }
    };

    loadConsoleText();

    return () => {
      isActive = false;
    };
  }, [jenkinsApi, projects, selectedJob, entityRef.kind, entityRef.name, entityRef.namespace]);

  if (isLoadingProjects) {
    return (
      <InfoCard title="Latest Jenkins Console Output">
        <Typography variant="body2">Loading Jenkins jobs...</Typography>
      </InfoCard>
    );
  }

  if (error) {
    return (
      <InfoCard title="Latest Jenkins Console Output">
        <Typography color="error">{error}</Typography>
      </InfoCard>
    );
  }

  if (!projects.length) {
    return (
      <InfoCard title="Latest Jenkins Console Output">
        <Typography variant="body2">
          No Jenkins jobs were returned for this entity.
        </Typography>
      </InfoCard>
    );
  }

  const selectedProject =
    projects.find(
      (project: JenkinsProjectSummary) => project.fullName === selectedJob,
    ) ?? projects[0];

  if (!selectedProject) {
    return (
      <InfoCard title="Latest Jenkins Console Output">
        <Typography variant="body2">
          No Jenkins jobs were returned for this entity.
        </Typography>
      </InfoCard>
    );
  }

  return (
    <InfoCard title="Latest Jenkins Console Output">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Select Job
          </Typography>
          <Grid container spacing={1}>
            {projects.map((project: JenkinsProjectSummary) => (
              <Grid item key={project.fullName}>
                <Button
                  variant={
                    project.fullName === selectedProject.fullName
                      ? 'contained'
                      : 'outlined'
                  }
                  color="primary"
                  onClick={() => setSelectedJob(project.fullName)}
                >
                  {project.displayName}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" color="textSecondary">
            Showing build #{selectedProject.lastBuild?.number ?? 'N/A'} for{' '}
            {selectedProject.displayName}
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {selectedProject.lastBuild?.url ? (
            <Button
              variant="outlined"
              color="primary"
              href={selectedProject.lastBuild.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open Build in Jenkins
            </Button>
          ) : null}
        </Grid>
        <Grid item xs={12}>
          {isLoadingConsole ? (
            <Typography variant="body2">Loading console output...</Typography>
          ) : consoleText ? (
            <Typography
              component="pre"
              variant="body2"
              style={{
                margin: 0,
                padding: 16,
                maxHeight: 480,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                borderRadius: 4,
                backgroundColor: '#111827',
                color: '#f9fafb',
                fontFamily: 'monospace',
              }}
            >
              {consoleText}
            </Typography>
          ) : (
            <Typography variant="body2">
              No console output is available for the selected build.
            </Typography>
          )}
        </Grid>
      </Grid>
    </InfoCard>
  );
};

const AboutCardNoTechDocs = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const annotations = entity.metadata.annotations ?? {};
  const docsUrl = annotations['amfs.io/docs-url'];
  const sourceLocation = annotations['amfs.io/source-location'];

  return (
   <InfoCard title="About" variant={variant}>
      {docsUrl || sourceLocation ? (
        <>
          <Grid container spacing={1}>
            {docsUrl ? (
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  href={docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Docs
                </Button>
              </Grid>
            ) : null}
            {sourceLocation ? (
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  href={sourceLocation}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View Source
                </Button>
              </Grid>
            ) : null}
          </Grid>
          <br />
          <br />
        </>
      ) : null}
      <AboutContent entity={entity} />
    </InfoCard>
  );
};

// const docsContent = <DocsCustomContent />;

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isJenkinsAvailable}>
      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <EntityJenkinsContent />
        </Grid>
        <Grid item xs={12}>
          <JenkinsConsoleOutputCard />
        </Grid>
      </Grid>
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No Jenkins pipeline available for this entity"
        missing="info"
        description="Add the jenkins.io/job-full-name annotation in your catalog entity to show Jenkins pipeline history in this tab."
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const architectureContent = <ArchitectureDiagramCard />;

const sonarQubeContent = (
  <EntitySonarQubeContentPage
    missingAnnotationReadMoreUrl={SONARQUBE_READ_MORE_URL}
  />
);

const sonarQubeSystemContent = (
  <SonarQubeRelatedEntitiesOverview
    relationType={RELATION_HAS_PART}
    entityKind="component"
  />
);

const securityContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isCheckmarxAvailable}>
      <CheckmarxSecurityContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState
        title="No Checkmarx scan results available for this entity"
        missing="info"
        description={`Add the ${CHECKMARX_SOURCE_LOCATION_ANNOTATION} annotation pointing to the source repository in your catalog entity to show Checkmarx One scan results in this tab.`}
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const securitySystemContent = <CheckmarxSystemOverview />;

const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

const overviewContent = (
  <Grid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <Grid item md={6}>
      {/* <EntityAboutCard variant="gridItem" /> */}
      <AboutCardNoTechDocs variant="gridItem" />
    </Grid>
    <Grid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </Grid>

    <Grid item md={4} xs={12}>
      <EntityLinksCard />
    </Grid>
    {/* <EntitySwitch>
      <EntitySwitch.Case if={isJenkinsAvailable}>
        <Grid item md={4} xs={12}>
          <EntityLatestJenkinsRunCard branch="main,master" variant="gridItem" />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch> */}
    <Grid item md={8} xs={12}>
      <EntityHasSubcomponentsCard variant="gridItem" />
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/sbom" title="SBOM">
      <JenkinsSbomCard />
    </EntityLayout.Route>

    <EntityLayout.Route path="/code-quality" title="Code Quality">
      {sonarQubeContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/security" title="Security">
      {securityContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityProvidedApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityConsumedApisCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

     {/* <EntityLayout.Route path="/docs" title="Docs">
      {docsContent}
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/sbom" title="SBOM">
      <JenkinsSbomCard />
    </EntityLayout.Route>

    <EntityLayout.Route path="/code-quality" title="Code Quality">
      {sonarQubeContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/security" title="Security">
      {securityContent}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/kubernetes"
      title="Kubernetes"
      if={isKubernetesAvailable}
    >
      <EntityKubernetesContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6}>
          <EntityDependsOnComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityDependsOnResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/docs" title="Docs">
      {docsContent}
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    {/* <EntityLayout.Route path="/docs" title="Docs">
      {docsContent}
    </EntityLayout.Route> */}

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard /> */}
          <AboutCardNoTechDocs />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard variant="gridItem" /> */}
          <AboutCardNoTechDocs variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard variant="gridItem" />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        variant="gridItem"
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>

    <EntityLayout.Route path="/code-quality" title="Code Quality">
      {sonarQubeSystemContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/security" title="Security">
      {securitySystemContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          {/* <EntityAboutCard variant="gridItem" /> */}
          <AboutCardNoTechDocs variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/architecture" title="Architecture">
      {architectureContent}
    </EntityLayout.Route>
  </EntityLayout>
);

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
